/**
 * Schema Zod para Onboarding de Motorista (Etapa 2)
 * Validação de Documentos, Endereço, Veículo, PIX e Declarações
 */

import { z } from 'zod';
import { validateCPF, validateCNH, validateRenavam } from '../utils/validators';

// Tipos de chave PIX disponíveis
export const PIX_KEY_TYPES = [
    { value: 'celular', label: 'Celular' },
    { value: 'email', label: 'E-mail' },
    { value: 'cpf', label: 'CPF' },
    { value: 'cnpj', label: 'CNPJ' },
    { value: 'aleatoria', label: 'Chave Aleatória' },
] as const;

// Aplicativos de mobilidade disponíveis
export const MOBILITY_APPS = [
    'Uber',
    '99',
    'InDriver',
    'Cabify',
    'LadyDriver',
    'Waze Carpool',
    'Outras',
] as const;

// Cidades disponíveis - Grande Florianópolis, Balneário Camboriú e Itapema
export const AVAILABLE_CITIES = [
    { value: 'florianopolis', label: 'Florianópolis (Capital)' },
    { value: 'sao-jose', label: 'São José' },
    { value: 'palhoca', label: 'Palhoça' },
    { value: 'biguacu', label: 'Biguaçu' },
    { value: 'santo-amaro-imperatriz', label: 'Santo Amaro da Imperatriz' },
    { value: 'governador-celso-ramos', label: 'Governador Celso Ramos' },
    { value: 'antonio-carlos', label: 'Antônio Carlos' },
    { value: 'aguas-mornas', label: 'Águas Mornas' },
    { value: 'sao-pedro-alcantara', label: 'São Pedro de Alcântara' },
    { value: 'balneario-camboriu', label: 'Balneário Camboriú' },
    { value: 'itapema', label: 'Itapema' },
] as const;

export const onboardingSchema = z.object({
    // ============================================================
    // FOTO E DOCUMENTOS VISUAIS
    // ============================================================

    selfie_url: z
        .string()
        .min(1, 'Tire uma selfie do seu rosto'),

    app_profile_screenshot_url: z
        .string()
        .min(1, 'Envie o print do seu perfil no app de mobilidade'),

    cnh_photo_url: z
        .string()
        .min(1, 'Tire uma foto da sua CNH aberta'),

    // ============================================================
    // DADOS PESSOAIS
    // ============================================================

    cpf: z
        .string()
        .regex(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'Formato inválido. Use 999.999.999-99')
        .refine(validateCPF, 'CPF inválido'),

    rg: z
        .string()
        .min(5, 'RG deve ter no mínimo 5 caracteres')
        .max(20, 'RG muito longo'),

    cnh_number: z
        .string()
        .regex(/^\d{11}$/, 'CNH deve ter exatamente 11 dígitos')
        .refine(validateCNH, 'CNH inválida'),

    // ============================================================
    // ENDEREÇO
    // ============================================================

    zip_code: z
        .string()
        .regex(/^\d{5}-\d{3}$/, 'CEP inválido. Use o formato 99999-999'),

    street: z
        .string()
        .min(3, 'Rua deve ter no mínimo 3 caracteres')
        .max(150, 'Rua muito longa'),

    number: z
        .string()
        .min(1, 'Número é obrigatório')
        .max(10, 'Número muito longo'),

    complement: z
        .string()
        .max(100, 'Complemento muito longo')
        .optional()
        .or(z.literal('')),

    neighborhood: z
        .string()
        .min(2, 'Bairro deve ter no mínimo 2 caracteres')
        .max(100, 'Bairro muito longo'),

    city: z
        .string()
        .min(2, 'Cidade deve ter no mínimo 2 caracteres')
        .max(100, 'Cidade muito longa'),

    state: z
        .string()
        .length(2, 'UF deve ter exatamente 2 letras')
        .regex(/^[A-Z]{2}$/, 'UF inválida'),

    // ============================================================
    // DADOS DO VEÍCULO
    // ============================================================

    vehicle_plate: z
        .string()
        .regex(
            /^[A-Z]{3}-?\d[A-Z0-9]\d{2}$/,
            'Placa inválida. Use ABC-1234 ou ABC-1D23 (Mercosul)'
        )
        .transform((val) => val.toUpperCase()),

    vehicle_brand: z
        .string()
        .min(2, 'Selecione a marca do veículo'),

    vehicle_model: z
        .string()
        .min(2, 'Selecione o modelo do veículo'),

    vehicle_color: z
        .string()
        .min(3, 'Selecione a cor do veículo'),

    vehicle_year: z
        .number({ invalid_type_error: 'Ano deve ser um número' })
        .min(1990, 'Veículo muito antigo (mínimo 1990)')
        .max(new Date().getFullYear() + 1, 'Ano inválido'),

    renavam: z
        .string()
        .regex(/^\d{11}$/, 'Renavam deve ter exatamente 11 dígitos')
        .refine(validateRenavam, 'Renavam inválido'),

    // ============================================================
    // DADOS PIX E PROFISSIONAIS
    // ============================================================

    pix_key_type: z
        .string()
        .min(1, 'Selecione o tipo de chave PIX'),

    pix_key: z
        .string()
        .min(1, 'Informe sua chave PIX'),

    mobility_apps: z
        .array(z.string())
        .min(1, 'Selecione pelo menos um aplicativo'),

    operating_cities: z
        .array(z.string())
        .min(1, 'Selecione pelo menos uma cidade'),

    // ============================================================
    // DECLARAÇÕES OBRIGATÓRIAS
    // ============================================================

    confirm_driver_status: z
        .boolean()
        .refine(val => val === true, 'Você deve confirmar esta declaração'),

    confirm_tablet_investment: z
        .boolean()
        .refine(val => val === true, 'Você deve confirmar esta declaração'),

    confirm_contact_agreement: z
        .boolean()
        .refine(val => val === true, 'Você deve confirmar esta declaração'),

    confirm_info_accuracy: z
        .boolean()
        .refine(val => val === true, 'Você deve confirmar esta declaração'),
});

// Tipo inferido do schema
export type OnboardingFormData = z.infer<typeof onboardingSchema>;
